import { useEffect } from "react"
import parse from "html-react-parser"
import HCRender from "../hc-render"
import Typography from "aldoo-ra/Typography"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import { updateCDNUrls } from "aldoo-ra/HtmlEditor/cdn-urls"
import { BookOpenIcon } from "@heroicons/react/24/outline"

const calcReadTime = (html) => {
  if (!html) return null
  const text = html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()
  const words = text.split(" ").filter(Boolean).length
  const minutes = Math.max(1, Math.ceil(words / 200))
  return `${minutes} min`
}

const ArticleContent = ({ article, onFeedback }) => {
  const texts = TextBundle({ id: "help-center" })
  const ArticleFeedback = HCRender.getFragment("ArticleFeedback")

  if (!article) return null

  // the date in the page's language (the server renders English: no stored language there)
  const language = (() => {
    try {
      return (localStorage.getItem("current-site-language") || "en").replace(/"/g, "")
    } catch {
      return "en"
    }
  })()
  const formatDate = (dateString) => {
    if (!dateString) return ""
    return new Date(dateString).toLocaleDateString(language === "en" ? "en-US" : language, {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  const title = article.content.name || texts?.untitled_article || "Untitled Article"
  const lastUpdated = article.lastUpdated || article.updatedAt || article.createdAt
  const readTime = calcReadTime(article.content?.content)?.replace("min", texts?.minutes_short || "min")

  useEffect(() => {
    const prevTitle = document.title
    document.title = `${title} - ${texts?.help_center || "Help Center"}`
    return () => { document.title = prevTitle }
  }, [title])

  const getContent = () => {
    if (article.content && typeof article.content.content === "string") {
      const currentSlug = article.content.slug
      // Strip self-references: an article that links to itself becomes
      // styled text instead — the link is dropped.
      const replace = (node) => {
        if (node.type !== "tag" || node.name !== "a") return undefined
        const href = node.attribs?.href || ""
        const isSelf =
          currentSlug && (
            href === `/help-center/article/${currentSlug}` ||
            href.endsWith(`/article/${currentSlug}`) ||
            (typeof window !== "undefined" && href === window.location.pathname)
          )
        if (!isSelf) return undefined
        const textChildren = (node.children || [])
          .map((c) => (c.type === "text" ? c.data : ""))
          .join("")
        return <strong className="text-text">{textChildren}</strong>
      }
      return parse(updateCDNUrls(article.content.content), { replace })
    }
    return (
      <Typography variant="body1">
        {texts?.empty_article || "No content available for this article."}
      </Typography>
    )
  }

  return (
    <article className="hc">
      <div className="flex flex-col gap-5 pb-4">
        <Typography variant="h2" className="my-0">{title}</Typography>

        {readTime && (
          <div className="flex items-center gap-1 w-fit bg-primary_lt rounded-full px-3 h-8">
            <BookOpenIcon className="w-4 h-4 text-text" />
            <Typography variant="txt1_accent" className="text-text">{readTime}</Typography>
          </div>
        )}

        {lastUpdated && (
          <div className="flex items-center text-text_secondary mb-4">
            <Typography variant="txt1_dim" className="mr-1 my-0">
              {texts?.update_date || "Last updated on"}:
            </Typography>
            <time dateTime={lastUpdated} suppressHydrationWarning>{formatDate(lastUpdated)}</time>
          </div>
        )}
      </div>

      {/* the article's own HTML (the CMS editor's or an import's): headings, paragraphs and lists read as text */}
      <div className="article_content max-w-[860px] text-[16px] leading-relaxed text-text [&_h2]:mt-8 [&_h2]:mb-3 [&_h2]:text-[22px] [&_h2]:font-bold [&_h2]:leading-tight [&_h3]:mt-6 [&_h3]:mb-2 [&_h3]:text-[18px] [&_h3]:font-bold [&_p]:my-3 [&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1.5 [&_li]:pl-1 [&_li>ul]:my-1 [&_strong]:font-semibold [&_a]:text-primary [&_a]:underline [&_img]:max-w-full [&_img]:h-auto">
        {getContent()}
      </div>

      {ArticleFeedback && <ArticleFeedback article={article} onFeedback={onFeedback} />}
    </article>
  )
}

export default ArticleContent
